import { Button } from '@startupquickstart/frontend';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';

interface PromptRuleFormProps {
  /** Called when the user submits a new rule. Receives the rule body text. */
  onSubmit: (body: string) => Promise<void>;
  /** Whether the form submission is pending */
  isPending?: boolean;
  /** Optional extra buttons rendered inline next to Add Rule */
  actions?: React.ReactNode;
}

/**
 * Form for adding a new prompt rule with a textarea and action buttons below.
 *
 * @param onSubmit - Async callback invoked with the rule body on submit.
 * @param isPending - Whether an async submission is in progress.
 * @returns A textarea with buttons underneath.
 */
export function PromptRuleForm({ onSubmit, isPending = false, actions }: PromptRuleFormProps) {
  const [body, setBody] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    await onSubmit(trimmed);
    setBody('');
    textareaRef.current?.focus();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Enter rule text..."
        disabled={isPending}
        rows={3}
        className="w-full text-sm border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background resize-none"
        aria-label="New rule text"
      />
      <div className="flex items-center justify-end gap-2">
        {actions}
        <Button
          type="submit"
          size="sm"
          disabled={!body.trim() || isPending}
          loading={isPending}
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </Button>
      </div>
    </form>
  );
}
